/**
 * Pipeline tab — bottom-anchored, virtualized, column-customizable grid over
 * the live Process Pipeline funded tab ("2026").
 *
 * - Loads the FULL padded grid (payrollGetPipelineGrid) and anchors the view
 *   at the bottom (newest funded loans); scrolling up reveals older rows.
 * - Client-side filters (search / month / LO / stamped status).
 * - Column visibility + order via the Columns drawer (localStorage prefs,
 *   see pipelineColumns.js).
 * - Inline cell editing with optimistic apply; the server re-verifies the
 *   row identity and the previous cell value (failed-precondition → reload).
 *   Engine-written columns are read-only (lock in the header).
 * - 60s content-fingerprint poll while this tab is active refreshes the view
 *   when someone edits the sheet (paused mid-edit; silent after own edits).
 */

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Input,
  Select,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ExternalLinkIcon,
  LockIcon,
  RepeatIcon,
  SettingsIcon,
} from '@chakra-ui/icons';
import { List, useListRef } from 'react-window';
import { payrollApi } from '../api';
import {
  columnMeta,
  defaultPrefs,
  editableText,
  formatCell,
  isEngineOwned,
  loadPrefs,
  move,
  savePrefs,
  toggleHidden,
  visibleColumns,
} from './pipelineColumns';

const ROW_HEIGHT = 34;
const LEAD_WIDTH = 92; // row number + open-in-calculator
const POLL_MS = 60000;
const SEARCH_COLUMNS = ['Client', 'Loan Number', 'Primary LO', 'Secondary LO', 'Lender', 'Investor', 'Comp Code'];

/** One virtualized row. Everything comes in via rowProps (no hooks here). */
const PipelineRow = memo(function PipelineRow({
  index, style, ariaAttributes,
  items, cols, editing, colors,
  onCellClick, onCommit, onCancel, onOpenInCalculator,
}) {
  const item = items[index];
  if (!item) return null;
  return (
    <Flex
      style={style}
      {...ariaAttributes}
      align="stretch"
      borderBottomWidth="1px"
      borderColor={colors.border}
      fontWeight={item.bold ? '600' : 'normal'}
      title={item.bold ? 'Manual row (bold in the sheet) — live runs skip it' : undefined}
      _hover={{ bg: colors.rowHover }}
    >
      <Flex w={`${LEAD_WIDTH}px`} flexShrink={0} align="center" px={2} gap={1}>
        <Text fontSize="xs" color={colors.subtle} w="34px">{item.sheetRow}</Text>
        <IconButton
          aria-label="Open in calculator"
          icon={<ExternalLinkIcon />}
          size="xs"
          variant="ghost"
          onClick={() => onOpenInCalculator(item.sheetRow)}
        />
      </Flex>
      {cols.map((c) => {
        const value = item.cells[c.idx];
        const isEditing = editing && editing.sheetRow === item.sheetRow && editing.header === c.header;
        if (isEditing) {
          return (
            <Flex key={c.header} w={`${c.width}px`} flexShrink={0} align="center" px={1}>
              <Input
                size="xs"
                autoFocus
                defaultValue={editableText(c.header, value)}
                textAlign={c.align}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onCommit(item.sheetRow, c.header, e.target.value);
                  else if (e.key === 'Escape') onCancel();
                }}
                onBlur={(e) => onCommit(item.sheetRow, c.header, e.target.value)}
              />
            </Flex>
          );
        }
        const display = formatCell(c.header, value);
        const isBatchCol = String(c.header).trim() === 'Payroll Batch';
        return (
          <Box
            key={c.header}
            w={`${c.width}px`}
            flexShrink={0}
            px={2}
            lineHeight={`${ROW_HEIGHT - 1}px`}
            fontSize="sm"
            textAlign={c.align}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            title={c.editable ? display : `${display ? `${display} — ` : ''}written by payroll runs (read-only)`}
            cursor={c.editable ? 'pointer' : 'default'}
            onClick={c.editable ? () => onCellClick(item.sheetRow, c.header) : undefined}
            _hover={c.editable ? { bg: colors.cellHover, boxShadow: 'inset 0 0 0 1px var(--chakra-colors-blue-300)' } : undefined}
          >
            {isBatchCol && display
              ? <Badge fontSize="0.65em" colorScheme="green">{display}</Badge>
              : display}
          </Box>
        );
      })}
    </Flex>
  );
});

export default function PipelineTab({ isActive = false, refreshSignal = 0, onOpenInCalculator = () => {} }) {
  const toast = useToast();
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.800');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const cellHover = useColorModeValue('blue.50', 'whiteAlpha.100');
  const subtleColor = useColorModeValue('gray.500', 'gray.400');
  const colors = useMemo(
    () => ({ border: borderColor, headerBg, rowHover, cellHover, subtle: subtleColor }),
    [borderColor, headerBg, rowHover, cellHover, subtleColor],
  );

  const [grid, setGrid] = useState(null); // { tab, headers, rows, bold, fingerprint, asOf }
  const [prefs, setPrefs] = useState(null); // { order, hidden }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // { sheetRow, header, original }
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorTick, setAnchorTick] = useState(0);

  const [query, setQuery] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [loFilter, setLoFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const listRef = useListRef(null);
  const fingerprintRef = useRef(null);
  const editingRef = useRef(null);
  const savingRef = useRef(false);
  const pollBusyRef = useRef(false);
  const loadingRef = useRef(false);
  const lastSelfEditRef = useRef(0);

  useEffect(() => { editingRef.current = editing; }, [editing]);

  // Header name (trimmed) → 0-based cell index.
  const trimIdx = useMemo(() => {
    const m = new Map();
    (grid ? grid.headers : []).forEach((h, i) => {
      const t = String(h || '').trim();
      if (t && !m.has(t)) m.set(t, i);
    });
    return m;
  }, [grid]);

  const reload = useCallback(async ({ anchor = true } = {}) => {
    setLoading(true);
    loadingRef.current = true;
    setError(null);
    setEditing(null);
    editingRef.current = null;
    try {
      const res = await payrollApi.getPipelineGrid();
      fingerprintRef.current = res.fingerprint;
      setGrid(res);
      setPrefs((p) => p || loadPrefs(res.headers));
      if (anchor) setAnchorTick((t) => t + 1);
    } catch (err) {
      setError(err.message || 'Failed to load the pipeline.');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Page-level "Refresh data" button.
  useEffect(() => {
    if (!refreshSignal) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // 60s change-detection poll while this tab is active (paused mid-edit).
  useEffect(() => {
    if (!isActive) return undefined;
    const tick = async () => {
      if (document.hidden || pollBusyRef.current || editingRef.current || savingRef.current || loadingRef.current) return;
      pollBusyRef.current = true;
      try {
        const res = await payrollApi.getPipelineGrid();
        if (res.fingerprint !== fingerprintRef.current) {
          const selfEdit = Date.now() - lastSelfEditRef.current < 90000;
          fingerprintRef.current = res.fingerprint;
          setGrid(res);
          if (!selfEdit) {
            toast({ title: 'Pipeline sheet changed', description: 'View refreshed with the latest data.', status: 'info', duration: 3000, isClosable: true });
          }
        }
      } catch (e) { /* background poll — retry next tick */ } finally {
        pollBusyRef.current = false;
      }
    };
    const iv = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Persist column prefs.
  useEffect(() => { if (prefs) savePrefs(prefs); }, [prefs]);

  // Visible columns with their grid cell index.
  const cols = useMemo(() => {
    if (!grid || !prefs) return [];
    return visibleColumns(prefs)
      .map((header) => {
        const idx = grid.headers.indexOf(header);
        return { header, idx, ...columnMeta(header), editable: !isEngineOwned(header) };
      })
      .filter((c) => c.idx !== -1);
  }, [grid, prefs]);
  const totalWidth = LEAD_WIDTH + cols.reduce((s, c) => s + c.width, 0);

  // Filtered view (sheet order preserved — the timeline).
  const items = useMemo(() => {
    if (!grid) return [];
    const searchIdx = SEARCH_COLUMNS.map((n) => trimIdx.get(n)).filter((i) => i !== undefined);
    const monthI = trimIdx.get('Month');
    const loI = trimIdx.get('Primary LO');
    const batchI = trimIdx.get('Payroll Batch');
    const q = query.trim().toLowerCase();
    const out = [];
    grid.rows.forEach((cells, i) => {
      if (q && !searchIdx.some((ci) => String(cells[ci] === undefined || cells[ci] === null ? '' : cells[ci]).toLowerCase().includes(q))) return;
      if (monthFilter && String(cells[monthI] || '').trim() !== monthFilter) return;
      if (loFilter && String(cells[loI] || '').trim().toLowerCase() !== loFilter.toLowerCase()) return;
      const stamped = batchI !== undefined && String(cells[batchI] || '').trim() !== '';
      if (statusFilter === 'open' && stamped) return;
      if (statusFilter === 'stamped' && !stamped) return;
      out.push({ sheetRow: i + 2, cells, stamped, bold: grid.bold ? !!grid.bold[i + 1] : false });
    });
    return out;
  }, [grid, trimIdx, query, monthFilter, loFilter, statusFilter]);

  // Filter dropdown options.
  const months = useMemo(() => {
    const mi = trimIdx.get('Month');
    if (!grid || mi === undefined) return [];
    const seen = [];
    grid.rows.forEach((cells) => {
      const v = String(cells[mi] || '').trim();
      if (v && !seen.includes(v)) seen.push(v);
    });
    return seen;
  }, [grid, trimIdx]);
  const los = useMemo(() => {
    const li = trimIdx.get('Primary LO');
    if (!grid || li === undefined) return [];
    const seen = new Map();
    grid.rows.forEach((cells) => {
      const v = String(cells[li] || '').trim();
      if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), v);
    });
    return [...seen.values()].sort();
  }, [grid, trimIdx]);

  // Bottom anchor — on load / manual reload / filter change (not on silent poll refreshes).
  useEffect(() => {
    if (!items.length) return undefined;
    const raf = requestAnimationFrame(() => {
      try {
        if (listRef.current) listRef.current.scrollToRow({ index: items.length - 1, align: 'end', behavior: 'instant' });
      } catch (e) { /* list not mounted yet */ }
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorTick]);
  useEffect(() => {
    if (grid) setAnchorTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, monthFilter, loFilter, statusFilter]);

  // ---- Inline editing -------------------------------------------------------
  const onCellClick = useCallback((sheetRow, header) => {
    setEditing((cur) => {
      if (cur && cur.sheetRow === sheetRow && cur.header === header) return cur;
      return { sheetRow, header };
    });
  }, []);
  const onCancel = useCallback(() => {
    editingRef.current = null;
    setEditing(null);
  }, []);

  const gridRef = useRef(null);
  useEffect(() => { gridRef.current = grid; }, [grid]);
  const trimIdxRef = useRef(trimIdx);
  useEffect(() => { trimIdxRef.current = trimIdx; }, [trimIdx]);

  const onCommit = useCallback(async (sheetRow, header, draftText) => {
    const cur = editingRef.current;
    if (!cur || cur.sheetRow !== sheetRow || cur.header !== header) return; // blur after Enter already handled
    editingRef.current = null;
    setEditing(null);

    const g = gridRef.current;
    if (!g) return;
    const gi = sheetRow - 2;
    const colIdx = g.headers.indexOf(header);
    if (gi < 0 || gi >= g.rows.length || colIdx === -1) return;
    const original = g.rows[gi][colIdx];
    if (draftText === editableText(header, original)) return; // unchanged

    const meta = columnMeta(header);
    let value;
    if (draftText.trim() === '') {
      value = '';
    } else if (meta.type === 'money' || meta.type === 'number') {
      const n = Number(draftText.replace(/[$,\s]/g, ''));
      if (Number.isNaN(n)) {
        toast({ title: `"${draftText}" is not a number`, description: `${String(header).trim()} takes numeric values.`, status: 'error', duration: 5000, isClosable: true });
        return;
      }
      value = n;
    } else {
      value = draftText;
    }

    const ti = trimIdxRef.current;
    const prev = {
      value: original === undefined ? '' : original,
      client: g.rows[gi][ti.get('Client')],
      loanNumber: g.rows[gi][ti.get('Loan Number')],
    };

    // Optimistic apply.
    setGrid((cg) => {
      if (!cg) return cg;
      const rows = [...cg.rows];
      rows[gi] = [...rows[gi]];
      rows[gi][colIdx] = value;
      return { ...cg, rows };
    });

    savingRef.current = true;
    lastSelfEditRef.current = Date.now();
    try {
      await payrollApi.updatePipelineCell({ rowNum: sheetRow, header, value, prev });
      toast({ title: 'Saved', status: 'success', duration: 1500 });
    } catch (err) {
      setGrid((cg) => {
        if (!cg) return cg;
        const rows = [...cg.rows];
        rows[gi] = [...rows[gi]];
        rows[gi][colIdx] = original;
        return { ...cg, rows };
      });
      const stale = err && (err.code === 'functions/failed-precondition' || err.code === 'failed-precondition');
      toast({
        title: stale ? 'Row changed on the sheet' : 'Save failed',
        description: stale ? 'Reloading the latest data — please retry your edit.' : (err.message || String(err)),
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
      if (stale) reload({ anchor: false });
    } finally {
      savingRef.current = false;
    }
  }, [toast, reload]);

  // ---- Render ---------------------------------------------------------------
  if (loading && !grid) {
    return <Flex justify="center" py={16}><Spinner size="lg" /></Flex>;
  }
  if (error && !grid) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Text flex={1}>{error}</Text>
        <Button size="sm" onClick={() => reload()}>Retry</Button>
      </Alert>
    );
  }
  if (!grid) return null;

  return (
    <Box>
      {/* Toolbar */}
      <Flex gap={2} mb={3} wrap="wrap" align="center">
        <Input
          placeholder="Search client, loan #, LO, lender, investor, comp code…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          size="sm"
          maxW="330px"
        />
        <Select size="sm" maxW="140px" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
          <option value="">All months</option>
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </Select>
        <Select size="sm" maxW="220px" value={loFilter} onChange={(e) => setLoFilter(e.target.value)}>
          <option value="">All LOs</option>
          {los.map((lo) => <option key={lo} value={lo}>{lo}</option>)}
        </Select>
        <Select size="sm" maxW="130px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All rows</option>
          <option value="open">Open only</option>
          <option value="stamped">Stamped only</option>
        </Select>
        <Text fontSize="xs" color={colors.subtle}>
          {items.length} of {grid.rows.length} rows
          {grid.asOf ? ` · as of ${new Date(grid.asOf).toLocaleTimeString()}` : ''}
        </Text>
        <Box flex={1} />
        <IconButton aria-label="Reload" icon={<RepeatIcon />} size="sm" variant="outline" isLoading={loading} onClick={() => reload()} />
        <Button size="sm" variant="outline" leftIcon={<SettingsIcon />} onClick={() => setDrawerOpen(true)}>
          Columns
        </Button>
      </Flex>

      {/* Grid */}
      <Box borderWidth="1px" borderColor={colors.border} borderRadius="md" overflowX="auto">
        <Box width={`${totalWidth}px`} minWidth="100%">
          {/* Header row */}
          <Flex bg={colors.headerBg} borderBottomWidth="2px" borderColor={colors.border}>
            <Box w={`${LEAD_WIDTH}px`} flexShrink={0} px={2} py={2} fontSize="xs" fontWeight="bold" color={colors.subtle}>
              Row
            </Box>
            {cols.map((c) => (
              <Flex
                key={c.header}
                w={`${c.width}px`}
                flexShrink={0}
                px={2}
                py={2}
                fontSize="xs"
                fontWeight="bold"
                color={colors.subtle}
                justify={c.align === 'right' ? 'flex-end' : 'flex-start'}
                align="center"
                gap={1}
                title={c.editable ? String(c.header).trim() : `${String(c.header).trim()} — written by payroll runs (read-only)`}
                whiteSpace="nowrap"
                overflow="hidden"
              >
                <Text as="span" isTruncated>{String(c.header).trim()}</Text>
                {!c.editable && <LockIcon boxSize={2.5} />}
              </Flex>
            ))}
          </Flex>
          {/* Virtualized rows */}
          {items.length === 0 ? (
            <Flex justify="center" py={10}>
              <Text fontSize="sm" color={colors.subtle}>No rows match the filters.</Text>
            </Flex>
          ) : (
            <Box height="calc(100vh - 380px)" minHeight="360px">
              <List
                listRef={listRef}
                rowComponent={PipelineRow}
                rowCount={items.length}
                rowHeight={ROW_HEIGHT}
                rowProps={{
                  items,
                  cols,
                  editing,
                  colors,
                  onCellClick,
                  onCommit,
                  onCancel,
                  onOpenInCalculator,
                }}
                style={{ height: '100%', width: '100%' }}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Text fontSize="xs" color={colors.subtle} mt={2}>
        Click a cell to edit — changes write straight to the Google Sheet and are audited.
        Locked columns are written by payroll runs. Bold rows are manual (live runs skip them).
      </Text>

      {/* Columns drawer */}
      <Drawer isOpen={drawerOpen} placement="right" size="sm" onClose={() => setDrawerOpen(false)}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Columns</DrawerHeader>
          <DrawerBody>
            <Text fontSize="xs" color={colors.subtle} mb={3}>
              Check to show, use the arrows to reorder. Saved on this browser.
            </Text>
            {prefs && prefs.order.map((header, idx) => {
              const hidden = prefs.hidden.includes(header);
              return (
                <HStack key={header} py={1} spacing={2}>
                  <Checkbox
                    isChecked={!hidden}
                    onChange={() => setPrefs((p) => toggleHidden(p, header))}
                  />
                  <Text fontSize="sm" flex={1} color={hidden ? colors.subtle : undefined} isTruncated>
                    {String(header).trim()}
                  </Text>
                  {isEngineOwned(header) && <LockIcon boxSize={2.5} color={colors.subtle} />}
                  <IconButton
                    aria-label="Move up"
                    icon={<ArrowUpIcon />}
                    size="xs"
                    variant="ghost"
                    isDisabled={idx === 0}
                    onClick={() => setPrefs((p) => move(p, header, -1))}
                  />
                  <IconButton
                    aria-label="Move down"
                    icon={<ArrowDownIcon />}
                    size="xs"
                    variant="ghost"
                    isDisabled={idx === prefs.order.length - 1}
                    onClick={() => setPrefs((p) => move(p, header, +1))}
                  />
                </HStack>
              );
            })}
          </DrawerBody>
          <DrawerFooter>
            <Button size="sm" variant="outline" onClick={() => setPrefs(defaultPrefs(grid.headers))}>
              Reset to defaults
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
